import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import openSocket from "../../services/socket-io";

const ContactPresenceContext = createContext({
	byTicketId: {},
	byContactId: {},
	getPresence: () => null,
});

const TYPING_TTL_MS = 12000;

export const ContactPresenceProvider = ({ children }) => {
	const [byTicketId, setByTicketId] = useState({});
	const [byContactId, setByContactId] = useState({});
	const timersRef = useRef({});

	const applyPresence = (data) => {
		if (!data) return;
		const next = {
			presence: data.presence || "offline",
			lastSeen: data.lastSeen || null,
			participant: data.participant || null,
			chatId: data.chatId,
			updatedAt: Date.now(),
		};
		setByTicketId(prev => {
			const copy = { ...prev };
			(data.ticketIds || []).forEach(id => {
				copy[id] = next;
			});
			return copy;
		});
		if (data.contactId) {
			setByContactId(prev => ({ ...prev, [data.contactId]: next }));
		}

		const isTyping = next.presence === "typing" || next.presence === "recording";
		const keys = [
			...(data.ticketIds || []).map(id => `t-${id}`),
			data.contactId ? `c-${data.contactId}` : null,
		].filter(Boolean);
		keys.forEach(key => {
			if (timersRef.current[key]) clearTimeout(timersRef.current[key]);
			if (isTyping) {
				timersRef.current[key] = setTimeout(() => {
					const fallback = { ...next, presence: "online" };
					if (key.startsWith("t-")) {
						const id = Number(key.slice(2));
						setByTicketId(prev => ({ ...prev, [id]: fallback }));
					} else {
						const id = Number(key.slice(2));
						setByContactId(prev => ({ ...prev, [id]: fallback }));
					}
				}, TYPING_TTL_MS);
			}
		});
	};

	useEffect(() => {
		const socket = openSocket();
		if (!socket) return undefined;
		socket.on("contactPresence", applyPresence);
		return () => {
			socket.off("contactPresence", applyPresence);
			Object.values(timersRef.current).forEach(t => clearTimeout(t));
		};
	}, []);

	const getPresence = (ticket) => {
		if (!ticket) return null;
		return byTicketId[ticket.id] || (ticket.contactId && byContactId[ticket.contactId]) || (ticket.contact?.id && byContactId[ticket.contact.id]) || null;
	};

	const value = useMemo(
		() => ({ byTicketId, byContactId, getPresence }),
		[byTicketId, byContactId]
	);

	return (
		<ContactPresenceContext.Provider value={value}>
			{children}
		</ContactPresenceContext.Provider>
	);
};

export const useContactPresence = (ticket) => {
	const { getPresence } = useContext(ContactPresenceContext);
	return getPresence(ticket);
};

export { ContactPresenceContext };
